/*======================================================================================================================
  * Bluebell: src/app/state/image-deltas/image-deltas.effects.ts
  * Last Edited: 17/07/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EffectHandlerService } from '@services/effect-handler.service';
import {
  deselectResults,
  selectAllResults,
  selectResults,
  setSelectedResults,
} from '@state/results/results.actions';
import { selectSelectedResultsIds } from '@state/results/results.selectors';
import { ApiService } from '@state/api.service';
import {
  listImageDeltas,
  listImageDeltasFailure,
  listImageDeltasSuccess,
  listImageDeltaAvailableVariables,
  listImageDeltaAvailableVariablesFailure,
  listImageDeltaAvailableVariablesSuccess,
  clearImageDeltaAvailableVariables,
  addImageDeltaFilter,
  removeImageDeltaFilter,
} from '@state/image-deltas/image-deltas.actions';
import { deselectPostproVariable, selectPostproVariable } from '@state/postpro/postpro.actions';
import { selectSelectedPostproType } from '@state/postpro/postpro.selectors';
import { switchMap } from 'rxjs';
import { filter, map } from 'rxjs/operators';


@Injectable()
export class ImageDeltasEffects {
  private actions$ = inject(Actions);
  private api = inject(ApiService);
  private store = inject(Store);
  private handler = inject(EffectHandlerService);


  loadImageDeltas$ = createEffect(() => this.actions$.pipe(
    ofType(listImageDeltas),
    switchMap(({ caseIds }) => this.api.listImageDeltas(caseIds).pipe(
      this.handler.effectResponse({
        retry: listImageDeltas({ caseIds }),
        success: ({ data, filters }) => listImageDeltasSuccess({ data, filters}),
        failure: listImageDeltasFailure(),
        failureMessage: 'Could not load image deltas'
      })
    ))
  ));

  loadImageDeltaVariablesAvailable$ = createEffect(() => this.actions$.pipe(
    ofType(listImageDeltaAvailableVariables),
    switchMap(({ caseIds }) => this.api.listImageDeltaVariablesAvailable(caseIds).pipe(
      this.handler.effectResponse({
        retry: listImageDeltaAvailableVariables({ caseIds }),
        success: (availableVariables: string[]) => listImageDeltaAvailableVariablesSuccess({ availableVariables }),
        failure: listImageDeltaAvailableVariablesFailure(),
        failureMessage: 'Could not load image delta variables available'
      })
    ))
  ));

  onResultSelection$ = createEffect(() => this.actions$.pipe(
    ofType(
      selectResults,
      selectAllResults,
      setSelectedResults,
      deselectResults,
    ),
    concatLatestFrom(() => this.store.select(selectSelectedResultsIds)),
    map(([, caseIds]: [unknown, string[]]) => caseIds?.length
      ? listImageDeltaAvailableVariables({ caseIds })
      : clearImageDeltaAvailableVariables()
    ),
  ));

  // Only when switching to image deltas from another postpro type trigger a load of the data
  loadImageDeltasOnVariableSelect$ = createEffect(() => this.actions$.pipe(
    ofType(selectPostproVariable),
    concatLatestFrom(() => [
      this.store.select(selectSelectedPostproType),
      this.store.select(selectSelectedResultsIds),
    ]),
    filter(([{ postproType }, previousPostproType]) => {
      return postproType === 'image_delta' && previousPostproType !== 'image_delta';
    }),
    map(([, , caseIds]) => listImageDeltas({ caseIds }))
  ));

  addSelectedVariableToFilter = createEffect(() => this.actions$.pipe(
    ofType(selectPostproVariable),
    filter(({ postproType }) => postproType === 'image_delta'),
    map(({ variable }) => addImageDeltaFilter({ filter: 'variables', value: variable }))
  ));

  onVariableDeselect$ = createEffect(() => this.actions$.pipe(
    ofType(deselectPostproVariable),
    filter(({ postproType }) => postproType === 'image_delta'),
    map(({ variable }) => removeImageDeltaFilter({ filter: 'variables', value: variable }))
  ));
}
